// hooks/useUsers.ts
import { useState, useEffect, useCallback, useMemo } from "react";
import { User } from "@/types/crm";
import { usersService } from "@/services/usersService";
import { useAuth } from "./useAuth";

// Asegúrate de que este tipo coincida con la estructura real de tu API
interface UserInputData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  estado?: number;
  is_owner?: boolean;
  role?: {
    id: string;
    name: string;
    permissions: Record<string, any>;
  };
  created_at?: string;
  updated_at?: string;
  sales_target?: number;
  target_type?: string;
  calculated_percentage?: number | string;
  pipeline_objective?: number;
}

// Tipo para User de CRM si tiene role como string
interface CRMUser extends Omit<User, "role" | "rol"> {
  rol?: {
    id: string;
    name: string;
    permissions: Record<string, any>;
    description: string;
    is_system_role: boolean;
    company_id: string;
    slug: string;
    created_at: string;
    updated_at: string;
  };
  role?: string; // role como string alternativo
  sales_target?: number;
  target_type?: string;
  calculated_percentage?: number | string;
  pipeline_objective?: number;
}

export function useUsers() {
  const { user: currentUser, company, can } = useAuth();

  const [users, setUsers] = useState<CRMUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  });

  // Transformar datos de API a CRMUser
  const transformApiUserToCRMUser = useCallback(
    (apiUser: UserInputData): CRMUser => {
      const firstName = apiUser.first_name || "";
      const lastName = apiUser.last_name || "";

      return {
        id: apiUser.id,
        nombre: `${firstName} ${lastName}`.trim(),
        first_name: firstName,
        last_name: lastName,
        email: apiUser.email,
        phone: apiUser.phone || "",
        avatar_url: apiUser.avatar_url,
        estado: apiUser.estado !== undefined ? apiUser.estado : 1,
        is_owner: apiUser.is_owner || false,
        id_rol: apiUser.role?.id || "",
        empresa_administrativo_id: company?.id || "",
        fecha_creacion: apiUser.created_at || new Date().toISOString(),
        rol: apiUser.role
          ? {
              id: apiUser.role.id,
              name: apiUser.role.name,
              permissions: apiUser.role.permissions,
              description: "",
              is_system_role: false,
              company_id: company?.id || "",
              slug: company?.slug || "",
              created_at: apiUser.created_at || new Date().toISOString(),
              updated_at: apiUser.updated_at || new Date().toISOString(),
            }
          : undefined,
        role: apiUser.role?.name, // Mantener como string también si es necesario
        user_type: undefined,
        permissions: Object.keys(apiUser.role?.permissions || {}),
        sales_target: apiUser.sales_target,
        target_type: apiUser.target_type,
        calculated_percentage: apiUser.calculated_percentage,
        pipeline_objective: apiUser.pipeline_objective,
      } as CRMUser;
    },
    [company],
  );

  // Cargar usuarios
  const loadUsers = useCallback(
    async (page = 1) => {
      if (!company?.slug) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await usersService.getUsers(
          company.slug,
          page,
          pagination.limit,
          true, // Solo traer usuarios activos
        );

        console.log("🔥 [useUsers] 1. RAW API RESPONSE:", response);
        if (response.data && response.data.length > 0) {
          console.log(
            "🔥 [useUsers] 2. PRIMER USUARIO CRUDO (Check sales_target):",
            response.data[0],
          );
        } else {
          console.log("🔥 [useUsers] 2. NO HAY DATOS o ARRAY VACÍO");
        }

        // Transformar datos de API a CRMUser[]
        // Primero asegurarnos de que response.data es un array
        const data = Array.isArray(response.data) ? response.data : [];

        // Usar aserción explícita con verificación de tipo
        const transformedUsers = data.map((item: any) => {
          // Convertir a UserInputData
          const apiUser: UserInputData = {
            id: item.id || "",
            first_name: item.first_name || "",
            last_name: item.last_name || "",
            email: item.email || "",
            phone: item.phone,
            avatar_url: item.avatar_url || item.foto_url,
            estado: item.estado,
            is_owner: item.is_owner || false,
            role: item.role || item.rol,
            created_at: item.created_at || item.fecha_creacion,
            updated_at: item.updated_at,
            sales_target: item.sales_target,
            target_type: item.target_type,
            calculated_percentage: item.calculated_percentage,
            pipeline_objective: item.pipeline_objective,
          };
          return transformApiUserToCRMUser(apiUser);
        });

        console.log(
          "🔥 [useUsers] 3. USUARIOS TRANSFORMADOS (Final State):",
          transformedUsers,
        );

        setUsers(transformedUsers);
        setPagination(
          response.pagination || { page, limit: pagination.limit, total: 0 },
        );
      } catch (err: any) {
        console.error("❌ Error obteniendo usuarios:", err);
        setError(err.message || "Error al cargar los usuarios");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [company?.slug, pagination.limit, transformApiUserToCRMUser],
  );

  // Cargar usuarios inicialmente
  useEffect(() => {
    loadUsers(1);
  }, [loadUsers]);

  // Filtrar usuarios
  const filteredUsers = useMemo(() => {
    if (!searchQuery) {
      return users;
    }

    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        `${user.first_name || ""} ${user.last_name || ""}`
          .toLowerCase()
          .includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.phone?.toLowerCase().includes(query) ||
        user.rol?.name?.toLowerCase().includes(query),
    );
  }, [users, searchQuery]);

  // Estadísticas
  const statistics = useMemo(() => {
    const activeUsers = users.filter((user) => user.estado !== 0).length;
    const owners = users.filter((user) => user.is_owner).length;

    return {
      totalUsers: users.length,
      activeUsers,
      owners,
      inactiveUsers: users.length - activeUsers,
    };
  }, [users]);

  // Función auxiliar para convertir User a UserInputData
  const convertCRMUserToUserInputData = (user: CRMUser): UserInputData => {
    return {
      id: user.id,
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      phone: user.phone,
      avatar_url: user.avatar_url || user.foto_url,
      estado: user.estado,
      is_owner: user.is_owner,
      role: user.rol
        ? {
            id: user.rol.id,
            name: user.rol.name,
            permissions: user.rol.permissions,
          }
        : undefined,
      created_at: user.fecha_creacion,
      updated_at: user.fecha_creacion, // Usar mismo valor si no hay updated_at
      sales_target: user.sales_target,
      target_type: user.target_type,
      calculated_percentage: user.calculated_percentage,
      pipeline_objective: user.pipeline_objective,
    };
  };

  // Función auxiliar para convertir de ApiUser a UserInputData
  const convertApiUserToUserInputData = (apiUser: any): UserInputData => {
    return {
      id: apiUser.id,
      first_name: apiUser.first_name || "",
      last_name: apiUser.last_name || "",
      email: apiUser.email || "",
      phone: apiUser.phone,
      avatar_url: apiUser.avatar_url || apiUser.foto_url,
      estado: apiUser.estado,
      is_owner: apiUser.is_owner || false,
      role: apiUser.role || apiUser.rol,
      created_at: apiUser.created_at || apiUser.fecha_creacion,
      updated_at: apiUser.updated_at,
      sales_target: apiUser.sales_target,
      target_type: apiUser.target_type,
      calculated_percentage: apiUser.calculated_percentage,
      pipeline_objective: apiUser.pipeline_objective,
    };
  };

  // Crear usuario
  const createUser = useCallback(
    async (userData: {
      roleId: string;
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      phone?: string;
      avatar?: string;
      is_active?: boolean;
      role?: any;
      sales_target_value?: number;
      target_type?: string;
      pipeline_id?: string | null;
    }) => {
      if (!company?.slug || !currentUser?.id) {
        throw new Error("No hay compañía o usuario actual");
      }

      try {
        setLoading(true);

        const response = await usersService.createUser({
          companySlug: company.slug,
          creatorUserId: currentUser.id,
          ...userData,
        });

        // Convertir la respuesta a UserInputData
        const apiUserResponse = convertApiUserToUserInputData(response.data);
        const newUser = transformApiUserToCRMUser(apiUserResponse);
        if (userData.role && userData.role.name) {
          newUser.role = userData.role.name;
        }

        // Actualizar lista de usuarios
        setUsers((prev) => {
          const newUsers = [newUser, ...prev];
          return newUsers;
        });

        // Actualizar estadísticas
        setPagination((prev) => ({
          ...prev,
          total: prev.total + 1,
        }));

        return newUser;
      } catch (err: any) {
        console.error("❌ Error creando usuario:", err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [company?.slug, currentUser?.id, transformApiUserToCRMUser],
  );

  // Actualizar usuario
  const updateUser = useCallback(
    async (
      userId: string,
      userData: Partial<{
        roleId: string;
        email: string;
        firstName: string;
        lastName: string;
        phone?: string;
        estado?: number;
        avatar?: string;
        is_active?: boolean;
        pipeline_id?: string | null;
        sales_target_value?: number;
        target_type?: string;
      }>,
    ) => {
      if (!company?.slug || !currentUser?.id) {
        throw new Error("No hay compañía o usuario actual");
      }

      try {
        setLoading(true);

        const response = await usersService.updateUser(
          userId,
          company.slug,
          currentUser.id,
          userData,
        );

        // Convertir la respuesta a UserInputData
        const apiUserResponse = convertApiUserToUserInputData(response.data);
        const updatedUser = transformApiUserToCRMUser(apiUserResponse);

        // Actualizar en la lista
        setUsers((prev) =>
          prev.map((user) => (user.id === userId ? updatedUser : user)),
        );

        return updatedUser;
      } catch (err: any) {
        console.error("❌ Error actualizando usuario:", err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [company?.slug, currentUser?.id, transformApiUserToCRMUser],
  );

  // Eliminar usuario
  const deleteUser = useCallback(
    async (userToDelete: CRMUser) => {
      if (!company?.slug || !currentUser?.id) {
        throw new Error("No hay compañía o usuario actual");
      }

      try {
        setLoading(true);

        await usersService.deleteUser(
          userToDelete.id,
          company.slug,
          currentUser.id,
        );

        // Remover de la lista
        setUsers((prev) => {
          const newUsers = prev.filter((user) => user.id !== userToDelete.id);
          return newUsers;
        });

        // Actualizar estadísticas
        setPagination((prev) => ({
          ...prev,
          total: Math.max(0, prev.total - 1),
        }));
      } catch (err: any) {
        console.error("❌ Error eliminando usuario:", err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [company?.slug, currentUser?.id],
  );

  // Resetear contraseña
  const resetPassword = useCallback(
    async (userId: string, newPassword: string) => {
      try {
        setLoading(true);
        const result = await usersService.resetPassword(userId, newPassword);
        return result;
      } catch (err: any) {
        console.error("❌ Error reseteando contraseña:", err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Enviar email de bienvenida
  const sendWelcomeEmail = useCallback(async (userId: string) => {
    try {
      setLoading(true);
      const result = await usersService.sendWelcomeEmail(userId);
      return result;
    } catch (err: any) {
      console.error("❌ Error enviando email de bienvenida:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Verificar permisos
  const canManageUsers = useMemo(() => {
    // El owner siempre puede gestionar usuarios
    if (currentUser?.is_owner) return true;

    // Verificar permisos específicos
    return (
      can("users.create") || can("users.manage") || can("all.view") || can("*")
    );
  }, [can, currentUser?.is_owner]);

  const canEditUser = useCallback(
    (targetUser: CRMUser) => {
      // El owner puede editar a cualquiera
      if (currentUser?.is_owner) return true;

      // No puede editar a otro owner
      if (targetUser.is_owner) return false;

      // No puede editar a sí mismo (debería usar perfil personal)
      if (targetUser.id === currentUser?.id) return false;

      // Verificar permisos específicos
      return can("users.update") || can("all.view") || can("*");
    },
    [can, currentUser],
  );

  const canDeleteUser = useCallback(
    (targetUser: CRMUser) => {
      // El owner puede eliminar a cualquiera excepto a sí mismo
      if (currentUser?.is_owner) {
        return targetUser.id !== currentUser.id;
      }

      // No puede eliminar a un owner
      if (targetUser.is_owner) return false;

      // No puede eliminar a sí mismo
      if (targetUser.id === currentUser?.id) return false;

      // Verificar permisos específicos
      return can("users.delete") || can("all.view") || can("*");
    },
    [can, currentUser],
  );

  return {
    users,
    filteredUsers,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    pagination,
    statistics,
    createUser,
    updateUser,
    deleteUser,
    resetPassword,
    sendWelcomeEmail,
    canManageUsers,
    canEditUser,
    canDeleteUser,
    refreshUsers: () => loadUsers(pagination.page),
    loadPage: loadUsers,
  };
}
